export type RoutineCategory = 'email' | 'review' | 'breakdown' | 'journal' | 'custom';

export interface RoutineMeta {
  category: RoutineCategory;
  [key: string]: string;
}

export interface ParsedDescription {
  meta: RoutineMeta | null;
  displayText: string;
}

/**
 * Parse a description field that may contain META:: prefix.
 * Returns structured meta data and the human-readable description text.
 */
export function parseMetaDescription(description: string | null): ParsedDescription {
  if (!description || !description.startsWith('META::')) {
    return { meta: null, displayText: description ?? '' };
  }

  const separatorIdx = description.indexOf('\n---\n');
  if (separatorIdx === -1) {
    // Only META, no display text
    try {
      const jsonStr = description.slice(6); // Remove 'META::'
      const meta = JSON.parse(jsonStr) as RoutineMeta;
      return { meta, displayText: '' };
    } catch {
      return { meta: null, displayText: description };
    }
  }

  const jsonStr = description.slice(6, separatorIdx); // Between 'META::' and '---'
  const displayText = description.slice(separatorIdx + 5); // After '\n---\n'

  try {
    const meta = JSON.parse(jsonStr) as RoutineMeta;
    return { meta, displayText };
  } catch {
    return { meta: null, displayText: description };
  }
}

/**
 * Build a description string with META:: prefix from structured data.
 */
export function buildMetaDescription(meta: RoutineMeta, displayText: string): string {
  return `META::${JSON.stringify(meta)}\n---\n${displayText}`;
}

/**
 * Build a prompt from the selected category and field values.
 */
export function buildPromptFromCategory(category: RoutineCategory, fields: Record<string, string>): string {
  switch (category) {
    case 'email': {
      const filter = fields.emailFilter || 'in:inbox is:unread';
      const categories = fields.emailCategories || 'Dringend, Review, Archiv';
      const action = fields.emailAction || 'summary';

      let prompt = `Du bist ein Email-Triage-Assistent.\n\nAufgabe: Gehe durch die Emails mit folgendem Filter: ${filter}\n\nKategorisiere jede Email in eine der folgenden Kategorien: ${categories}\n`;

      if (action === 'todos' || action === 'both') {
        prompt += '\nErstelle fuer jede dringende Email ein To-Do mit Titel und Beschreibung.\n';
      }

      prompt += '\nErstelle am Ende eine Zusammenfassung: Wie viele Emails pro Kategorie, welche sind dringend.';
      return prompt;
    }

    case 'review': {
      const scope = fields.reviewScope || 'all';
      const scopeLabels: Record<string, string> = {
        prs: 'offene Pull Requests',
        commits: 'die letzten Commits',
        issues: 'offene Issues',
        all: 'Pull Requests, Commits und Issues',
      };
      const focus = fields.reviewFocus || 'Code-Qualitaet';

      return `Du bist ein Code-Review-Assistent.\n\nAufgabe: Pruefe ${scopeLabels[scope] || scope}.\n\nAchte besonders auf: ${focus}\n\nErstelle eine Zusammenfassung mit den wichtigsten Findings und Empfehlungen.`;
    }

    case 'breakdown':
      return 'Fuehre den Goal Breakdown fuer die aktuellen Ziele durch. Verwende die bestehende Breakdown-Logik.';

    case 'journal': {
      const journalType = fields.journalType || 'daily';
      const typeLabels: Record<string, string> = {
        daily: 'Tagesreflexion',
        weekly: 'Wochenreview',
        monthly: 'Monatsreview',
        free: 'Freie Reflexion',
      };
      const hint = fields.journalPromptHint || '';

      let prompt = `Du bist ein Journal-Assistent fuer eine ${typeLabels[journalType] || journalType}.\n`;

      if (hint) {
        prompt += `\nKontext / Leitfragen: ${hint}\n`;
      }

      prompt += '\nStelle reflektierende Fragen und fasse die Erkenntnisse zusammen.';
      return prompt;
    }

    case 'custom':
      return fields.prompt || '';
  }
}

/**
 * Auto-generate a display text for the description based on category and fields.
 */
export function buildDisplayText(category: RoutineCategory, fields: Record<string, string>): string {
  switch (category) {
    case 'email': {
      const action = fields.emailAction || 'summary';
      const actionLabels: Record<string, string> = {
        summary: 'Nur zusammenfassen',
        todos: 'To-Dos erstellen',
        both: 'Zusammenfassen und To-Dos erstellen',
      };
      return `Email Triage - ${actionLabels[action] || action}`;
    }
    case 'review':
      return `Code Review - ${fields.reviewFocus || 'Allgemein'}`;
    case 'breakdown':
      return 'Goal Breakdown - Automatische Ziel-Aufschluesselung';
    case 'journal': {
      const typeLabels: Record<string, string> = {
        daily: 'Tagesreflexion',
        weekly: 'Wochenreview',
        monthly: 'Monatsreview',
        free: 'Freie Reflexion',
      };
      return `Journal - ${typeLabels[fields.journalType] || 'Reflexion'}`;
    }
    case 'custom':
      return fields.prompt ? fields.prompt.slice(0, 80) : 'Benutzerdefinierte Routine';
  }
}

/**
 * Calculate the next execution time from a cron expression.
 * Supports standard 5-field cron: minute hour dom month dow
 */
export function getNextExecution(schedule: string, timezone: string): Date | null {
  const parts = schedule.trim().split(/\s+/);
  if (parts.length !== 5) return null;

  const [minStr, hourStr, domStr, , dowStr] = parts;
  const min = parseInt(minStr, 10);
  const hour = parseInt(hourStr, 10);
  if (isNaN(min) || isNaN(hour)) return null;

  // Create a date in the timezone context
  const now = new Date();
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone: timezone || 'Europe/Berlin',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
  const tzParts = formatter.formatToParts(now);
  const get = (type: string) => parseInt(tzParts.find(p => p.type === type)?.value ?? '0', 10);

  const nowYear = get('year');
  const nowMonth = get('month') - 1;
  const nowDay = get('day');
  const nowHour = get('hour');
  const nowMinute = get('minute');

  // Daily pattern: specific time every day
  if (domStr === '*' && dowStr === '*') {
    const candidate = new Date(nowYear, nowMonth, nowDay, hour, min, 0);
    if (hour > nowHour || (hour === nowHour && min > nowMinute)) {
      return candidate;
    }
    // Next day
    candidate.setDate(candidate.getDate() + 1);
    return candidate;
  }

  // Weekly pattern
  if (domStr === '*' && dowStr !== '*') {
    const targetDow = parseInt(dowStr, 10);
    if (isNaN(targetDow)) return null;

    const nowDate = new Date(nowYear, nowMonth, nowDay, hour, min, 0);
    const currentDow = nowDate.getDay();

    let daysUntil = targetDow - currentDow;
    if (daysUntil < 0) daysUntil += 7;
    if (daysUntil === 0 && (hour < nowHour || (hour === nowHour && min <= nowMinute))) {
      daysUntil = 7;
    }

    const candidate = new Date(nowYear, nowMonth, nowDay + daysUntil, hour, min, 0);
    return candidate;
  }

  // Monthly pattern
  if (dowStr === '*' && domStr !== '*') {
    const targetDom = parseInt(domStr, 10);
    if (isNaN(targetDom)) return null;

    let candidate = new Date(nowYear, nowMonth, targetDom, hour, min, 0);
    if (targetDom < nowDay || (targetDom === nowDay && (hour < nowHour || (hour === nowHour && min <= nowMinute)))) {
      candidate = new Date(nowYear, nowMonth + 1, targetDom, hour, min, 0);
    }
    return candidate;
  }

  return null;
}

/**
 * Format a relative time string like "in 2h 15min"
 */
export function formatRelativeTime(target: Date): string {
  const now = new Date();
  const diffMs = target.getTime() - now.getTime();
  if (diffMs <= 0) return 'jetzt';

  const diffMin = Math.floor(diffMs / 60000);
  if (diffMin < 60) return `in ${diffMin}min`;

  const hours = Math.floor(diffMin / 60);
  const mins = diffMin % 60;
  if (hours < 24) {
    return mins > 0 ? `in ${hours}h ${mins}min` : `in ${hours}h`;
  }

  const days = Math.floor(hours / 24);
  return `in ${days}d`;
}

/**
 * Check if a date is today.
 */
export function isToday(date: Date | string): boolean {
  const d = typeof date === 'string' ? new Date(date) : date;
  const now = new Date();
  return d.getFullYear() === now.getFullYear() &&
    d.getMonth() === now.getMonth() &&
    d.getDate() === now.getDate();
}

export const ROUTINE_CATEGORIES: { value: RoutineCategory; label: string; description: string }[] = [
  { value: 'email', label: 'Email Triage', description: 'Emails sortieren, kategorisieren, To-Dos erstellen' },
  { value: 'review', label: 'Code Review', description: 'Repository pruefen, Issues identifizieren' },
  { value: 'breakdown', label: 'Goal Breakdown', description: 'Ziele herunterbrechen' },
  { value: 'journal', label: 'Journal / Reflexion', description: 'Tagebuch-Prompts, Weekly Reviews' },
  { value: 'custom', label: 'Benutzerdefiniert', description: 'Freies Prompt-Feld' },
];
